import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { PageHeader } from '../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import axiosInstance from '@/utils/axiosInstance';
import { ArrowLeft, ExternalLink, Edit, Trash2, CheckCircle2, XCircle, Plus, X, Check, Flag, Download } from 'lucide-react';
import { downloadProductWorkbook } from '../utils/productExport';
import ReactCountryFlag from 'react-country-flag';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from '@/components/ui/chart';
import { DataTable } from '@/components/ui/data-table';
import { useResellerImages } from '@/hooks/useResellerImages';
import { getCountryFlagCode } from '../utils/countryFlags';
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Globe } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '../hooks/use-toast';

// Note: getResellerFavicon is now provided via props from the parent component
// which uses the useResellerImages hook to fetch images from the database

// Custom Legend Component that is clickable with strikethrough for disabled items
// Memoized to prevent unnecessary re-renders
const CustomChartLegend = memo(({ payload, onClick, config, disabledResellers, t, getResellerFavicon }) => {
  return (
    <div className="flex flex-wrap items-center justify-center gap-4 pt-3">
      {payload.map((entry) => {
        const itemConfig = config[entry.dataKey] || {};
        const isLamptwist = entry.dataKey?.toLowerCase().includes('lamptwist');
        const color = isLamptwist ? 'hsl(47 96% 53%)' : itemConfig.color || 'hsl(var(--chart-1))';
        const isDisabled = disabledResellers?.includes(entry.dataKey);
        const favicon = getResellerFavicon?.(entry.dataKey);
        
        return (
          <button
            key={entry.dataKey}
            onClick={() => onClick && onClick(entry.dataKey)}
            className="flex items-center gap-2 rounded-sm px-2 py-1 hover:bg-muted transition-colors cursor-pointer"
          >
            <div
              className="h-2 w-2 rounded-sm"
              style={{ 
                backgroundColor: color,
                opacity: isDisabled ? 0.5 : 1
              }}
            />
            {favicon && (
              <img
                src={favicon}
                alt={t ? t('resellerFavicon') : 'Reseller favicon'}
                className="w-3 h-3 rounded"
                style={{ opacity: isDisabled ? 0.5 : 1 }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <span 
              className={`text-xs ${isDisabled ? 'line-through text-muted-foreground' : 'text-muted-foreground'}`}
            >
              {entry.value}
            </span>
          </button>
        );
      })}
    </div>
  );
});

// Custom Tooltip Component that filters out disabled resellers
// Memoized to prevent unnecessary re-renders
const CustomTooltipContent = memo(({ active, payload, label, disabledResellers, t, getResellerFavicon }) => {
  if (!active || !payload) return null;

  // Filter out disabled resellers from payload
  const filteredPayload = payload.filter(entry => !disabledResellers?.includes(entry.dataKey));

  if (filteredPayload.length === 0) return null;

  return (
    <div
      className="rounded-xl border p-3 shadow-lg"
      style={{ backgroundColor: 'hsl(var(--background))', zIndex: 50, position: 'relative' }}
    >
      <div className="grid gap-2.5">
        <p className="text-sm font-semibold text-foreground mb-1">{label}</p>
        {filteredPayload.map((entry, index) => {
          const isLamptwist = entry.dataKey?.toLowerCase().includes('lamptwist');
          const color = isLamptwist ? 'hsl(47 96% 53%)' : entry.color || 'hsl(var(--chart-1))';
          const favicon = getResellerFavicon?.(entry.dataKey);
          const value = typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value;
          
          return (
            <div key={entry.dataKey} className="flex items-center gap-2.5">
              <div
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: color }}
              />
              {favicon && (
                <img
                  src={favicon}
                  alt={t ? t('resellerFavicon') : 'Reseller favicon'}
                  className="w-4 h-4 rounded"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <span className="text-xs text-muted-foreground">{entry.name}:</span>
              <span className="text-xs font-semibold text-foreground">{value}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
});

// Function to get chart color avoiding yellow colors similar to Lamptwist
const getChartColor = (idx) => {
  const chartNum = (idx % 7) + 1; // Use 7 colors to avoid yellow tones
  // Skip colors 4, 5 and 6 which are too close to yellow
  const adjustedNum = chartNum <= 3 ? chartNum : chartNum + 2;
  return `hsl(var(--chart-${(adjustedNum - 1) % 5 + 1}))`;
};

function ProductDetail() {
  const { t } = useTranslation('productDetail');
  const navigate = useNavigate();
  const { getResellerImage } = useResellerImages();
  const [searchParams] = useSearchParams();
  const { shopifyId: shopifyIdParam, customId } = useParams();
  const shopifyIdFromQuery = searchParams.get('id');
  const [shopifyId, setShopifyId] = useState(shopifyIdFromQuery || shopifyIdParam || null);
  const [isCustomProduct, setIsCustomProduct] = useState(!!customId);
  const hasNavigationHistory = useRef(false);

  const [product, setProduct] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedResellers, setSelectedResellers] = useState([]);
  const [disabledResellers, setDisabledResellers] = useState([]);
  const [timeRange, setTimeRange] = useState('90d');
  const [exporting, setExporting] = useState(false);
  const handleExportExcel = async () => {
    if (!filteredHistory.length || exporting) return;
    setExporting(true);
    try {
      await downloadProductWorkbook({ product, filteredHistory, timeRange, t });
    } catch (err) {
      toast({
        title: t('exportError'),
        description: err?.message || '',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  };
  const [productUrls, setProductUrls] = useState({ twistscrape: [], brand_custom: [] });
  const [loadingUrls, setLoadingUrls] = useState(false);
  const [showAddUrlModal, setShowAddUrlModal] = useState(false);
  const [availableResellers, setAvailableResellers] = useState([]);
  const [newUrl, setNewUrl] = useState({ url: '', ean: '' });
  const [urlInputs, setUrlInputs] = useState([{ id: 1, url: '', status: null }]); // Multi-URL support
  const [addingUrl, setAddingUrl] = useState(false);
  const [urlError, setUrlError] = useState('');
  const [showEditUrlModal, setShowEditUrlModal] = useState(false);
  const [editingUrl, setEditingUrl] = useState(null);
  const [updatingUrl, setUpdatingUrl] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingUrlId, setDeletingUrlId] = useState(null);
  const [deletingUrl, setDeletingUrl] = useState(false);
  const { toast } = useToast();

  // Report modal states
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportIssues, setReportIssues] = useState({
    wrong_rrp: false,
    wrong_ean: false,
    wrong_vendor_code: false,
  });
  const [reportRrp, setReportRrp] = useState('');
  const [reportEan, setReportEan] = useState('');
  const [reportVendorCode, setReportVendorCode] = useState('');
  const [hasExistingReports, setHasExistingReports] = useState(false);
  const [existingReportTypes, setExistingReportTypes] = useState({
    wrong_rrp: false,
    wrong_ean: false,
    wrong_vendor_code: false
  });
  const [submittingReport, setSubmittingReport] = useState(false);
  const [reportedValues, setReportedValues] = useState({
    rrp: null,
    ean: null,
    vendor_code: null
  });

  useEffect(() => {
    // Check if there's navigation history (user came from another page in the app)
    hasNavigationHistory.current = window.history.length > 1;
  }, []);

  useEffect(() => {
    const resolveAndFetch = async () => {
      try {
        setLoading(true);

        // Check if it's a custom product
        if (customId) {
          // Load custom product
          await fetchCustomProduct(customId);
          await fetchAvailableResellers();
          setLoading(false);
          return;
        }

        // Directly use shopify_id from URL params (no need to resolve from handle anymore)
        const idToUse = shopifyIdFromQuery || shopifyIdParam;
        if (!idToUse) {
          setError(t('productIdRequired'));
          setLoading(false);
          return;
        }
        if (idToUse) setShopifyId(idToUse);
        await fetchProductHistory(idToUse);
        await fetchProductUrls(idToUse);
        await fetchAvailableResellers();
      } catch (e) {
        setError(t('error'));
      } finally {
        setLoading(false);
      }
    };
    resolveAndFetch();
  }, [shopifyIdParam, shopifyIdFromQuery]);

  const fetchCustomProduct = async (id) => {
    try {
      const response = await axiosInstance.get(`/api/custom-products/${id}/detail/`);

      // Transform custom product data to match regular product structure
      const productData = {
        p_title: response.data.name,
        v_variant_title: response.data.variant_name || '',
        v_ean: response.data.ean,
        v_vendor_code: response.data.vendor_code || '',
        v_compareAtPrice: response.data.rrp_incl_vat,
        p_vendor: response.data.brand,
        v_image_link: null,
        is_custom: true
      };

      setProduct(productData);

      // No history for custom products yet
      setHistory([]);
      setSelectedResellers([]);

      // Load URLs for custom product (using EAN)
      // Note: We might need to create a custom URL fetching method for custom products

      // Load existing reports
      await loadExistingReports(productData);

      setError(null);
    } catch (err) {
      console.error('Error fetching custom product:', err);
      setError(t('error'));
    }
  };

  const fetchProductHistory = async (id) => {
    try {
      const response = await axiosInstance.get(`/api/product/history/?shopify_id=${id ?? shopifyId}`);

      setProduct(response.data.product);
      setHistory(response.data.history || []);

      // Extract unique resellers from history
      const uniqueResellers = [...new Set(
        response.data.history.map(item => item.hostname)
      )];
      setSelectedResellers(uniqueResellers);

      // Load existing reports
      await loadExistingReports(response.data.product);

      setError(null);
    } catch (err) {
      setError(t('error'));
    } finally {}
  };

  const fetchProductUrls = async (id) => {
    try {
      setLoadingUrls(true);
      const response = await axiosInstance.get(`/api/products/${id}/urls/`);
      setProductUrls(response.data.urls || { twistscrape: [], brand_custom: [] });
    } catch (err) {
      console.error('Error fetching product URLs:', err);
      // Don't show error for URLs, just log it
    } finally {
      setLoadingUrls(false);
    }
  };

  const handleDeleteUrl = async (urlId) => {
    setDeletingUrlId(urlId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUrl = async () => {
    try {
      setDeletingUrl(true);
      await axiosInstance.delete(`/api/products/${shopifyId}/urls/${deletingUrlId}/delete/`);
      toast({
        title: t('success'),
        description: t('urls.deleteSuccess'),
      });
      await fetchProductUrls(shopifyId);
    } catch (err) {
      console.error('Error deleting URL:', err);
      toast({
        title: t('error'),
        description: err.response?.data?.error || t('urls.deleteError'),
        variant: 'destructive',
      });
    } finally {
      setDeletingUrl(false);
      setShowDeleteConfirm(false);
      setDeletingUrlId(null);
    }
  };

  const handleEditUrl = async (urlItem) => {
    setEditingUrl({
      id: urlItem.id,
      url: urlItem.url,
      is_active: urlItem.is_active,
      reseller_domain: urlItem.reseller_domain
    });
    setUrlError('');
    setShowEditUrlModal(true);
  };

  const handleUpdateUrl = async () => {
    setUrlError('');
    
    if (!editingUrl.url.trim()) {
      setUrlError(t('urls.urlRequired'));
      return;
    }

    try {
      setUpdatingUrl(true);
      await axiosInstance.patch(`/api/products/${shopifyId}/urls/${editingUrl.id}/`, {
        url: editingUrl.url.trim(),
        is_active: editingUrl.is_active
      });
      toast({
        title: t('success'),
        description: t('urls.updateSuccess'),
      });
      await fetchProductUrls(shopifyId);
      setShowEditUrlModal(false);
      setEditingUrl(null);
    } catch (err) {
      console.error('Error updating URL:', err);
      setUrlError(err.response?.data?.error || t('urls.updateError'));
    } finally {
      setUpdatingUrl(false);
    }
  };

  const fetchAvailableResellers = async () => {
    try {
      const response = await axiosInstance.get('/api/resellers/available/');
      setAvailableResellers(response.data.resellers || []);
    } catch (err) {
      console.error('Error fetching available resellers:', err);
    }
  };

  // Validate if URL belongs to an approved reseller
  const validateUrl = async (url) => {
    if (!url.trim()) return null;
    
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase().replace(/^www\./, '');
      
      // Check if hostname matches any available reseller
      // The reseller name might be just the domain name (e.g., 'lampenmaster')
      // while the hostname includes the extension (e.g., 'lampenmaster.be')
      const isValid = availableResellers.some(reseller => {
        const resellerName = (reseller.reseller || reseller.hostname || '').toLowerCase().replace(/^www\./, '');
        // Check exact match or if hostname starts with reseller name followed by a dot
        return hostname === resellerName || hostname.startsWith(resellerName + '.');
      });
      
      return isValid ? 'valid' : 'invalid';
    } catch (e) {
      return 'invalid';
    }
  };

  // Handle URL input change with validation
  const handleUrlInputChange = async (id, value) => {
    setUrlInputs(prevInputs => 
      prevInputs.map(input => 
        input.id === id ? { ...input, url: value, status: 'validating' } : input
      )
    );
    
    // Validate URL
    const status = await validateUrl(value);
    setUrlInputs(prevInputs => 
      prevInputs.map(input => 
        input.id === id ? { ...input, status } : input
      )
    );
  };

  // Add new URL input field
  const addUrlInput = () => {
    const newId = Math.max(...urlInputs.map(u => u.id), 0) + 1;
    setUrlInputs([...urlInputs, { id: newId, url: '', status: null }]);
  };

  // Remove URL input field
  const removeUrlInput = (id) => {
    if (urlInputs.length > 1) {
      setUrlInputs(urlInputs.filter(input => input.id !== id));
    }
  };

  const handleAddUrl = async () => {
    setUrlError('');
    
    // Filter valid URLs
    const validUrls = urlInputs.filter(input => input.url.trim() && input.status === 'valid');
    
    if (validUrls.length === 0) {
      setUrlError(t('urls.noValidUrls'));
      return;
    }

    // Get EAN from product or use the one from state
    const ean = product?.v_ean || newUrl.ean || '';
    if (!ean.trim()) {
      setUrlError(t('urls.eanRequired'));
      return;
    }

    try {
      setAddingUrl(true);
      
      // Add all valid URLs
      const results = await Promise.allSettled(
        validUrls.map(input => 
          axiosInstance.post(`/api/products/${shopifyId}/urls/add/`, {
            url: input.url.trim(),
            ean: ean.trim()
          })
        )
      );
      
      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failCount = results.filter(r => r.status === 'rejected').length;

      if (successCount > 0) {
        toast({
          title: t('success'),
          description: failCount > 0
            ? t('urls.addMultipleSuccessWithFailures', { successCount, failCount })
            : t('urls.addMultipleSuccess', { count: successCount }),
        });
        await fetchProductUrls(shopifyId);
        setUrlInputs([{ id: 1, url: '', status: null }]);
        setNewUrl({ url: '' });
        setUrlError('');
        setShowAddUrlModal(false);
      } else {
        // Extract error message from first failed request
        const firstError = results.find(r => r.status === 'rejected');
        const errorMessage = firstError?.reason?.response?.data?.error || t('urls.addError');
        setUrlError(errorMessage);
      }
    } catch (err) {
      console.error('Error adding URLs:', err);
      setUrlError(err.response?.data?.error || t('error'));
    } finally {
      setAddingUrl(false);
    }
  };

  // Filter history by time range
  const filteredHistory = React.useMemo(() => {
    if (!history.length) return [];

    const now = new Date();
    let daysToSubtract = 90;
    if (timeRange === '30d') {
      daysToSubtract = 30;
    } else if (timeRange === '7d') {
      daysToSubtract = 7;
    }

    const startDate = new Date(now);
    startDate.setDate(startDate.getDate() - daysToSubtract);

    // Map country to TLD for lamptwist display (gb -> uk to match real ccTLD)
    const countryToTld = { be: 'be', nl: 'nl', fr: 'fr', de: 'de', gb: 'uk', it: 'it' };

    return history
      .filter(item => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate;
      })
      .map(item => {
        if (item.hostname?.toLowerCase().includes('lamptwist') && item.country) {
          const tld = countryToTld[item.country.toLowerCase()] || 'com';
          return { ...item, hostname: `lamptwist.${tld}` };
        }
        return item;
      });
  }, [history, timeRange]);

  // Get unique resellers from filtered history
  const allResellers = React.useMemo(() => {
    return [...new Set(filteredHistory.map(item => item.hostname))].sort();
  }, [filteredHistory]);

  // Transform data for the chart with discounts
  const chartData = React.useMemo(() => {
    if (!filteredHistory.length) return [];

    // Group by date
    const groupedByDate = {};
    
    filteredHistory.forEach(item => {
      const date = item.date;
      if (!groupedByDate[date]) {
        groupedByDate[date] = {};
      }
      
      // Include all resellers and calculate discount (rounded to 2 decimals)
      if (item.compare_at_price) {
        const discount = parseFloat(item.discount) || 0;
        groupedByDate[date][item.hostname] = parseFloat(discount.toFixed(2));
      }
    });
    
    // Round all numeric values to 2 decimals
    Object.keys(groupedByDate).forEach(date => {
      Object.keys(groupedByDate[date]).forEach(reseller => {
        if (typeof groupedByDate[date][reseller] === 'number') {
          groupedByDate[date][reseller] = parseFloat(groupedByDate[date][reseller].toFixed(2));
        }
      });
    });

    // Convert to array format for chart
    return Object.keys(groupedByDate)
      .sort()
      .map(date => ({
        date,
        ...groupedByDate[date]
      }));
  }, [filteredHistory]);

  // Calculate min and max values for YAxis domain
  const discountRange = React.useMemo(() => {
    if (!chartData.length || !allResellers.length) return ['auto', 'auto'];
    
    const allDiscounts = chartData
      .flatMap(item => 
        allResellers
          .map(reseller => item[reseller])
          .filter(discount => discount != null && discount !== undefined)
      )
      .map(discount => parseFloat(discount));
    
    if (allDiscounts.length === 0) return ['auto', 'auto'];
    
    const min = Math.min(...allDiscounts);
    const max = Math.max(...allDiscounts);
    
    // Start from minimum discount, add 5% padding at top
    const padding = (max - min) * 0.05;
    
    return [min, max + padding];
  }, [chartData, allResellers]);

  // Calculate average discounts for the table
  const averageDiscounts = React.useMemo(() => {
    if (!filteredHistory.length || !allResellers.length) return [];
    
    const discounts = allResellers.map(reseller => {
      const resellerData = filteredHistory.filter(item => 
        item.hostname === reseller && item.discount != null
      );
      
      if (resellerData.length === 0) return { hostname: reseller, avgDiscount: 0 };
      
      const avg = resellerData.reduce((sum, item) => 
        sum + parseFloat(item.discount), 0
      ) / resellerData.length;
      
      return {
        hostname: reseller,
        avgDiscount: parseFloat(avg.toFixed(2))
      };
    });
    
    // Sort by avgDiscount descending (highest first)
    return discounts.sort((a, b) => b.avgDiscount - a.avgDiscount);
  }, [filteredHistory, allResellers]);

  // Calculate summary by market (country)
  const summaryByMarket = React.useMemo(() => {
    if (!filteredHistory.length) return {};
    
    // Group by country
    const byCountry = {};
    
    filteredHistory.forEach(item => {
      const country = item.country?.toUpperCase() || 'UNKNOWN';
      if (!byCountry[country]) {
        byCountry[country] = {};
      }
      
      // For each retailer, keep only the most recent entry
      const hostname = item.hostname;
      if (!byCountry[country][hostname]) {
        byCountry[country][hostname] = item;
      } else {
        // Compare dates and keep the most recent
        const existingDate = new Date(byCountry[country][hostname].date);
        const currentDate = new Date(item.date);
        if (currentDate > existingDate) {
          byCountry[country][hostname] = item;
        }
      }
    });
    
    // Convert to array format, sorted by discount descending
    const result = {};
    Object.keys(byCountry).forEach(country => {
      const retailers = Object.values(byCountry[country])
        .filter(item => item.price_reseller != null && item.discount != null)
        .sort((a, b) => {
          const discountA = parseFloat(a.discount) || 0;
          const discountB = parseFloat(b.discount) || 0;
          return discountB - discountA; // Descending order
        });
      
      if (retailers.length > 0) {
        result[country] = retailers;
      }
    });
    
    return result;
  }, [filteredHistory]);

  // Get the latest date from summaryByMarket for display
  const summaryLatestDate = React.useMemo(() => {
    if (!Object.keys(summaryByMarket).length) return null;

    let latestDate = null;
    Object.values(summaryByMarket).forEach(retailers => {
      retailers.forEach(item => {
        const itemDate = new Date(item.date);
        if (!latestDate || itemDate > latestDate) {
          latestDate = itemDate;
        }
      });
    });

    return latestDate;
  }, [summaryByMarket]);

  // Initialize selectedResellers with all resellers on mount
  React.useEffect(() => {
    if (allResellers.length > 0 && selectedResellers.length === 0) {
      setSelectedResellers(allResellers);
    }
  }, [allResellers.length]);

  // Memoized callback to handle legend click
  const handleLegendClick = useCallback((reseller) => {
    if (disabledResellers.includes(reseller)) {
      // Re-enable
      setDisabledResellers(prev => prev.filter(r => r !== reseller));
    } else {
      // Disable
      setDisabledResellers(prev => [...prev, reseller]);
    }
  }, [disabledResellers]);

  const formatPrice = (price, currency = '€') => {
    if (price === null || price === undefined) return '-';
    return `${parseFloat(price).toFixed(2)} ${currency}`;
  };

  const formatPriceByCountry = (price, countryCode) => {
    if (price === null || price === undefined) return '-';
    const currency = countryCode === 'GB' ? '£' : '€';
    return `${parseFloat(price).toFixed(2)} ${currency}`;
  };

  const formatDiscount = (discount) => {
    if (discount === null || discount === undefined) return '0%';
    const discountValue = parseFloat(discount);
    if (discountValue === 0) {
      return <span className="text-muted-foreground">0%</span>;
    }
    // Positive = discount (below RRP) -> red, no sign
    // Negative = above RRP -> green, show + sign (invert to show markup)
    return (
      <span className={discountValue < 0 ? 'text-green-600' : 'text-red-600'}>
        {discountValue < 0 ? '+' : ''}{Math.abs(discountValue).toFixed(2)}%
      </span>
    );
  };

  // Use the hook to get reseller images from DB
  const getResellerFaviconForTable = getResellerImage;

  // Helper function to extract base domain and extension
  const extractDomainInfo = (domain) => {
    if (!domain) return { base: null, extension: null, fullDomain: null };
    const parts = domain.split('.');
    if (parts.length < 2) return { base: domain, extension: null, fullDomain: domain };
    const extension = '.' + parts[parts.length - 1];
    const base = parts.slice(0, -1).join('.');
    return { base, extension, fullDomain: domain };
  };

  // Group URLs by base domain
  const groupUrlsByBaseDomain = (urls) => {
    const grouped = {};
    urls.forEach(urlItem => {
      const domain = urlItem.reseller_domain || extractDomainFromUrl(urlItem.original_url || urlItem.l_url || urlItem.url);
      if (!domain) return;
      
      const { base, extension, fullDomain } = extractDomainInfo(domain);
      if (!base) return;
      
      if (!grouped[base]) {
        grouped[base] = {
          baseDomain: base,
          extensions: new Set(),
          domains: new Set(),
          urls: []
        };
      }
      
      if (extension) {
        grouped[base].extensions.add(extension);
      }
      if (fullDomain) {
        grouped[base].domains.add(fullDomain);
      }
      grouped[base].urls.push(urlItem);
    });
    
    return Object.values(grouped).map(group => ({
      ...group,
      extensions: Array.from(group.extensions).sort(),
      domains: Array.from(group.domains).sort()
    }));
  };

  // Helper function to extract domain from URL
  const extractDomainFromUrl = (url) => {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace(/^www\./, '');
    } catch {
      return null;
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  // Helper function to get displayed value (reported or original)
  const getDisplayValue = (originalValue, reportedValue, hasDecoration = false) => {
    if (reportedValue !== null && reportedValue !== undefined) {
      if (hasDecoration) {
        return (
          <span className="font-semibold">
            {reportedValue}
          </span>
        );
      }
      return reportedValue;
    }
    return originalValue;
  };

  // Load existing reports for display on the page
  const loadExistingReports = async (productData) => {
    if (!productData) return;

    try {
      const productShopifyId = isCustomProduct ? null : (shopifyId || productData.shopify_id);
      const productEan = productData?.v_ean;

      let url = '/api/product-reports/list/?';
      if (productShopifyId) {
        url += `shopify_id=${productShopifyId}`;
      } else if (productEan) {
        url += `product_ean=${productEan}`;
      }

      if (productShopifyId || productEan) {
        const response = await axiosInstance.get(url);
        const existingReports = response.data;

        // Update states for button styling
        const hasReports = existingReports.length > 0;
        setHasExistingReports(hasReports);

        const reportTypes = {
          wrong_rrp: false,
          wrong_ean: false,
          wrong_vendor_code: false
        };

        const values = {
          rrp: null,
          ean: null,
          vendor_code: null
        };

        // Process reports to extract proposed values
        existingReports.forEach(report => {
          if (report.issue_type === 'wrong_rrp') {
            reportTypes.wrong_rrp = true;
            if (report.proposed_rrp) {
              values.rrp = report.proposed_rrp;
            }
          } else if (report.issue_type === 'wrong_ean') {
            reportTypes.wrong_ean = true;
            if (report.proposed_ean) {
              values.ean = report.proposed_ean;
            }
          } else if (report.issue_type === 'wrong_vendor_code') {
            reportTypes.wrong_vendor_code = true;
            if (report.proposed_vendor_code) {
              values.vendor_code = report.proposed_vendor_code;
            }
          }
        });

        setExistingReportTypes(reportTypes);
        setReportedValues(values);
      }
    } catch (error) {
      console.error('Error loading existing reports:', error);
    }
  };

  // Report modal functions
  const openReportModal = async () => {
    if (!product) return;

    // Initialize default values
    const currentRrp = product?.v_compareAtPrice ?? product?.compare_at_price ?? '';
    const defaultRrp = currentRrp !== null && currentRrp !== undefined ? String(currentRrp) : '';
    const defaultEan = product?.v_ean || '';
    const defaultVendorCode = product?.v_vendor_code || '';

    // Initialize with default values (will be overridden if existing reports found)
    let issues = {
      wrong_rrp: false,
      wrong_ean: false,
      wrong_vendor_code: false,
    };
    let existingTypes = {
      wrong_rrp: false,
      wrong_ean: false,
      wrong_vendor_code: false,
    };
    let proposedRrp = defaultRrp;
    let proposedEan = defaultEan;
    let proposedVendorCode = defaultVendorCode;
    let hasExisting = false;

    // Load existing reports for this product BEFORE opening modal
    try {
      const productShopifyId = isCustomProduct ? null : (shopifyId || product.shopify_id);
      const productEan = product?.v_ean;

      let url = '/api/product-reports/list/?';
      if (productShopifyId) {
        url += `shopify_id=${productShopifyId}`;
      } else if (productEan) {
        url += `product_ean=${productEan}`;
      }

      if (productShopifyId || productEan) {
        const response = await axiosInstance.get(url);
        const existingReports = response.data;

        hasExisting = existingReports.length > 0;

        // Pre-fill the form based on existing reports
        if (hasExisting) {
          existingReports.forEach(report => {
            if (report.issue_type === 'wrong_rrp') {
              issues.wrong_rrp = true;
              existingTypes.wrong_rrp = true;
              if (report.proposed_rrp) {
                proposedRrp = String(report.proposed_rrp);
              }
            } else if (report.issue_type === 'wrong_ean') {
              issues.wrong_ean = true;
              existingTypes.wrong_ean = true;
              if (report.proposed_ean) {
                proposedEan = report.proposed_ean;
              }
            } else if (report.issue_type === 'wrong_vendor_code') {
              issues.wrong_vendor_code = true;
              existingTypes.wrong_vendor_code = true;
              if (report.proposed_vendor_code) {
                proposedVendorCode = report.proposed_vendor_code;
              }
            }
          });
        }
      }
    } catch (error) {
      console.error('Error loading existing reports:', error);
    }

    // Set all state values at once BEFORE opening modal
    setHasExistingReports(hasExisting);
    setExistingReportTypes(existingTypes);
    setReportIssues(issues);
    setReportRrp(proposedRrp);
    setReportEan(proposedEan);
    setReportVendorCode(proposedVendorCode);

    // Now open modal with all data already loaded
    setReportModalOpen(true);
  };

  const closeReportModal = () => {
    setReportModalOpen(false);
    setReportIssues({ wrong_rrp: false, wrong_ean: false, wrong_vendor_code: false });
    setReportRrp('');
    setReportEan('');
    setReportVendorCode('');
    setHasExistingReports(false);
    setExistingReportTypes({ wrong_rrp: false, wrong_ean: false, wrong_vendor_code: false });
  };

  const deleteReport = async (issueType) => {
    if (!product) return;

    try {
      const productShopifyId = isCustomProduct ? null : (shopifyId || product.shopify_id);
      const productEan = product?.v_ean;

      let params = { issue_type: issueType };
      if (productShopifyId) {
        params.shopify_id = productShopifyId;
      } else if (productEan) {
        params.product_ean = productEan;
      }

      await axiosInstance.delete('/api/product-reports/delete/', { params });

      toast({
        description: t('report.reportCancelled'),
      });

      // Update local state to uncheck the box and mark as not existing
      setReportIssues(prev => ({ ...prev, [issueType]: false }));
      setExistingReportTypes(prev => ({ ...prev, [issueType]: false }));

      // Check if there are any remaining reports
      const remainingReports = Object.entries(existingReportTypes).some(
        ([key, value]) => key !== issueType && value
      );
      setHasExistingReports(remainingReports);

      // Clear the reported value for this field
      if (issueType === 'wrong_rrp') {
        setReportedValues(prev => ({ ...prev, rrp: null }));
        setReportRrp('');
      } else if (issueType === 'wrong_ean') {
        setReportedValues(prev => ({ ...prev, ean: null }));
        setReportEan('');
      } else if (issueType === 'wrong_vendor_code') {
        setReportedValues(prev => ({ ...prev, vendor_code: null }));
        setReportVendorCode('');
      }

      // Refresh product data
      if (isCustomProduct) {
        await fetchCustomProduct(customId);
      } else {
        await fetchProductHistory(shopifyId);
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      toast({
        variant: 'destructive',
        title: t('error'),
        description: t('report.failedToCancel'),
      });
    }
  };

  const deleteAllReports = async () => {
    if (!product) return;

    try {
      const productShopifyId = isCustomProduct ? null : (shopifyId || product.shopify_id);
      const productEan = product?.v_ean;

      // Get all existing report types
      const reportsToDelete = Object.entries(existingReportTypes)
        .filter(([_, exists]) => exists)
        .map(([type, _]) => type);

      if (reportsToDelete.length === 0) return;

      // Delete all reports
      await Promise.all(
        reportsToDelete.map(issueType => {
          let params = { issue_type: issueType };
          if (productShopifyId) {
            params.shopify_id = productShopifyId;
          } else if (productEan) {
            params.product_ean = productEan;
          }
          return axiosInstance.delete('/api/product-reports/delete/', { params });
        })
      );

      toast({
        description: t('report.reportCancelled'),
      });

      // Reset all states
      setReportIssues({ wrong_rrp: false, wrong_ean: false, wrong_vendor_code: false });
      setExistingReportTypes({ wrong_rrp: false, wrong_ean: false, wrong_vendor_code: false });
      setHasExistingReports(false);
      setReportedValues({ rrp: null, ean: null, vendor_code: null });

      // Refresh product data
      if (isCustomProduct) {
        await fetchCustomProduct(customId);
      } else {
        await fetchProductHistory(shopifyId);
      }
    } catch (error) {
      console.error('Error deleting reports:', error);
      toast({
        variant: 'destructive',
        title: t('error'),
        description: t('report.failedToCancel'),
      });
    }
  };

  const submitReport = async () => {
    if (!product) return;

    const reportsToSend = [];
    const productShopifyId = isCustomProduct ? null : (shopifyId || product.shopify_id);
    const productEan = product?.v_ean;

    // Validate that at least one issue is selected
    if (!reportIssues.wrong_rrp && !reportIssues.wrong_ean && !reportIssues.wrong_vendor_code) {
      toast({
        variant: 'destructive',
        title: t('error'),
        description: t('report.selectAtLeastOne'),
      });
      return;
    }

    // Build reports
    if (reportIssues.wrong_rrp) {
      const reportData = {
        issue_type: 'wrong_rrp',
        proposed_rrp: parseFloat(reportRrp),
        reported_url: null,
      };

      if (productShopifyId) reportData.shopify_id = productShopifyId;
      if (productEan) reportData.product_ean = productEan;
      if (product?.v_compareAtPrice) reportData.current_rrp = product.v_compareAtPrice;

      reportsToSend.push(reportData);
    }

    if (reportIssues.wrong_ean) {
      const reportData = {
        issue_type: 'wrong_ean',
        proposed_ean: reportEan.trim(),
        reported_url: null,
      };

      if (productShopifyId) reportData.shopify_id = productShopifyId;
      if (productEan) reportData.current_ean = productEan;

      reportsToSend.push(reportData);
    }

    if (reportIssues.wrong_vendor_code) {
      const reportData = {
        issue_type: 'wrong_vendor_code',
        proposed_vendor_code: reportVendorCode.trim(),
        reported_url: null,
      };

      if (productShopifyId) reportData.shopify_id = productShopifyId;
      if (productEan) reportData.product_ean = productEan;
      if (product?.v_vendor_code) reportData.current_vendor_code = product.v_vendor_code;

      reportsToSend.push(reportData);
    }

    try {
      setSubmittingReport(true);

      // Send all reports
      await Promise.all(
        reportsToSend.map(reportData =>
          axiosInstance.post('/api/product-reports/', reportData)
        )
      );

      toast({
        description: t('report.reportSubmitted'),
      });

      closeReportModal();

      // Refresh product data
      if (isCustomProduct) {
        await fetchCustomProduct(customId);
      } else {
        await fetchProductHistory(shopifyId);
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      toast({
        variant: 'destructive',
        title: t('error'),
        description: error.response?.data?.error || t('report.failedToSubmit'),
      });
    } finally {
      setSubmittingReport(false);
    }
  };

  // Handle back navigation
  const handleBackNavigation = () => {
    // Check if the page was opened in a new tab or has no navigation history
    if (!hasNavigationHistory.current || window.history.length <= 1) {
      // No history, redirect to products page with priceMonitoring tab
      navigate('/products?tab=priceMonitoring');
    } else {
      // Has history, go back to previous page
      navigate(-1);
    }
  };

  // Average Discounts Table Component
  const AverageDiscountsTable = ({ data, t, getResellerFaviconForTable }) => {
    const columns = [
      {
        header: t('table.reseller'),
        accessorKey: 'hostname',
        cell: (info) => {
          const hostname = info.getValue();
          const favicon = getResellerFaviconForTable(hostname);
          return (
            <div className="flex items-center gap-2">
              {favicon && (
                <img
                  src={favicon}
                  alt={t('resellerFavicon')}
                  className="w-4 h-4 rounded"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <span className="font-medium text-sm">{hostname}</span>
            </div>
          );
        },
      },
      {
        header: t('table.avgDiscount'),
        accessorKey: 'avgDiscount',
        cell: (info) => {
          const v = info.getValue();
          return (
            <span className={`text-sm font-medium ${v < 0 ? 'text-green-600' : v === 0 ? 'text-muted-foreground' : 'text-red-600'}`}>
              {v < 0 ? '+' : ''}{Math.abs(Number(v)).toFixed(2)}%
            </span>
          );
        },
      },
    ];

    // Show pagination only if there are more than 15 resellers
    const shouldShowPagination = data.length >= 15;
    
    return <DataTable data={data} columns={columns} pagination={shouldShowPagination ? { pageSize: 10 } : null} />;
  };

  return (
    <Layout>
      <PageHeader
        title={
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackNavigation}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-3xl font-bold">{t('title')}</h1>
          </div>
        }
      />
      <div className="space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-red-500">{error}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Product Info */}
            {product && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">{t('productInfo')}</CardTitle>
                  <Button
                    variant={hasExistingReports ? "destructive" : "outline"}
                    size="sm"
                    onClick={openReportModal}
                    className="flex items-center gap-2"
                  >
                    <Flag className="h-4 w-4" />
                    {t('report.button')}
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className={isCustomProduct ? "flex flex-col gap-4" : "grid grid-cols-1 md:grid-cols-2 gap-6"}>
                    {/* Product info */}
                    <div className="flex items-start gap-4">
                      {product.v_image_link && (
                        <img
                          src={product.v_image_link}
                          alt={product.p_title}
                          className="w-24 h-24 object-cover rounded"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/96';
                          }}
                        />
                      )}
                      <div className="space-y-1 flex-1">
                        <h2 className="text-xl font-bold">{product.p_title}</h2>
                        {product.v_variant_title && (
                          <p className="text-sm text-muted-foreground">{product.v_variant_title}</p>
                        )}
                        {/* EAN + Code fournisseur + RRP sur la même ligne pour custom products */}
                        {isCustomProduct ? (
                          <div className="flex items-center gap-4 flex-wrap text-sm text-muted-foreground">
                            {(product.v_ean || reportedValues.ean) && (
                              <span>
                                <strong>{t('eanLabel')}:</strong> {getDisplayValue(product.v_ean, reportedValues.ean, true)}
                              </span>
                            )}
                            {(product.v_vendor_code || reportedValues.vendor_code) && (
                              <span>
                                <strong>{t('vendorCode')}:</strong> {getDisplayValue(product.v_vendor_code, reportedValues.vendor_code, true)}
                              </span>
                            )}
                            {(product.v_compareAtPrice || reportedValues.rrp) && (
                              <span>
                                <strong>{t('rrpLabel')}:</strong> <span className={reportedValues.rrp ? 'font-semibold' : ''}>€{parseFloat(getDisplayValue(product.v_compareAtPrice, reportedValues.rrp, false)).toFixed(2)}</span>
                              </span>
                            )}
                          </div>
                        ) : (
                          <>
                            {(product.v_vendor_code || reportedValues.vendor_code) && (
                              <p className="text-sm text-muted-foreground">
                                <strong>{t('vendorCode')}:</strong> {getDisplayValue(product.v_vendor_code, reportedValues.vendor_code, true)}
                              </p>
                            )}
                            {(product.v_ean || reportedValues.ean) && (
                              <p className="text-sm text-muted-foreground">
                                <strong>{t('eanLabel')}:</strong> {getDisplayValue(product.v_ean, reportedValues.ean, true)}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    {/* Right column: RRP (EUR and GBP only) - Only for regular products */}
                    {!isCustomProduct && (
                      <div>
                        <h3 className="text-sm font-semibold mb-3">{t('rrpByCountry')}</h3>
                        <div className="flex flex-col gap-3">
                          {/* EUR (BE) */}
                          {(product.compareAt_BE || reportedValues.rrp) && (
                            <div className="flex items-center gap-2">
                              <ReactCountryFlag
                                countryCode="BE"
                                svg
                                style={{
                                  width: '1.5em',
                                  height: '1.5em',
                                }}
                              />
                              <span className="text-sm font-medium">
                                {t('rrpEur')}: {reportedValues.rrp ? (
                                  <span className="font-semibold">
                                    €{parseFloat(reportedValues.rrp).toFixed(2)}
                                  </span>
                                ) : (
                                  formatPriceByCountry(product.compareAt_BE, 'BE')
                                )}
                              </span>
                            </div>
                          )}
                          {/* GBP (UK) */}
                          {(product.compareAt_UK || reportedValues.rrp) && (
                            <div className="flex items-center gap-2">
                              <ReactCountryFlag
                                countryCode="GB"
                                svg
                                style={{
                                  width: '1.5em',
                                  height: '1.5em',
                                }}
                              />
                              <span className="text-sm font-medium">
                                {t('rrpGbp')}: {reportedValues.rrp ? (
                                  <span className="font-semibold">
                                    £{(parseFloat(reportedValues.rrp) * 0.85).toFixed(2)}
                                  </span>
                                ) : (
                                  formatPriceByCountry(product.compareAt_UK, 'GB')
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Chart */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between w-full border-b pb-5 px-6">
                <CardTitle className="text-lg">{t('discountHistory')}</CardTitle>
                {!isCustomProduct && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 border-amber-300 text-amber-800 hover:bg-amber-50 hover:text-amber-900"
                      disabled={!filteredHistory.length || exporting}
                      onClick={handleExportExcel}
                    >
                      <Download className="h-4 w-4" />
                      {exporting ? t('exporting') : t('exportExcel')}
                    </Button>
                    <Select value={timeRange} onValueChange={setTimeRange}>
                      <SelectTrigger className="w-[160px] rounded-lg">
                        <SelectValue placeholder={t('timeRange.90d')} />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl">
                        <SelectItem value="90d" className="rounded-lg">
                          {t('timeRange.90d')}
                        </SelectItem>
                        <SelectItem value="30d" className="rounded-lg">
                          {t('timeRange.30d')}
                        </SelectItem>
                        <SelectItem value="7d" className="rounded-lg">
                          {t('timeRange.7d')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {isCustomProduct || chartData.length === 0 ? (
                  <div className="py-12 text-center">
                    <p className="text-muted-foreground">
                      {isCustomProduct ? t('noDataAvailable') : t('noData')}
                    </p>
                  </div>
                ) : (
                  <ChartContainer
                    config={allResellers.reduce((acc, reseller, index) => {
                      const isLamptwist = reseller.toLowerCase().includes('lamptwist');
                      const color = isLamptwist 
                        ? 'hsl(47 96% 53%)' // #fed14d equivalent
                        : getChartColor(index);
                      
                      acc[reseller] = {
                        label: reseller,
                        color: color,
                      };
                      return acc;
                    }, {})}
                    className="h-[400px] w-full min-h-[400px]"
                  >
                    <LineChart
                      data={chartData}
                      margin={{
                        left: 20,
                        right: 20,
                        top: 20,
                        bottom: 10,
                      }}
                    >
                      <CartesianGrid 
                        vertical={false} 
                        strokeDasharray="3 3"
                        stroke="#e5e7eb"
                        opacity={0.6}
                      />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={formatDate}
                        tickLine={true}
                        axisLine={true}
                        tickMargin={12}
                        tick={{ fill: 'hsl(var(--foreground))', fontSize: 12, fontWeight: 500 }}
                        stroke="#6b7280"
                      />
                      <YAxis 
                        tickLine={true}
                        axisLine={true}
                        tickMargin={12}
                        domain={discountRange}
                        tick={{ fill: 'hsl(var(--foreground))', fontSize: 12, fontWeight: 500 }}
                        stroke="#6b7280"
                        tickFormatter={(value) => {
                          if (typeof value === 'number') {
                            return `${value.toFixed(2)}%`;
                          }
                          return value;
                        }}
                      />
                      <ChartTooltip
                        cursor={{ stroke: 'hsl(var(--border))', strokeWidth: 1, strokeDasharray: '5 5' }}
                        wrapperStyle={{ zIndex: 50, outline: 'none' }}
                        content={
                          <CustomTooltipContent disabledResellers={disabledResellers} t={t} getResellerFavicon={getResellerFaviconForTable} />
                        }
                      />
                      <ChartLegend
                        content={<CustomChartLegend
                          payload={allResellers.map((reseller, index) => ({
                            dataKey: reseller,
                            value: reseller,
                          }))}
                          onClick={handleLegendClick}
                        config={allResellers.reduce((acc, reseller, index) => {
                        const isLamptwist = reseller.toLowerCase().includes('lamptwist');
                        const color = isLamptwist
                          ? 'hsl(47 96% 53%)'
                          : getChartColor(index);

                          acc[reseller] = { label: reseller, color };
                          return acc;
                        }, {})}
                          disabledResellers={disabledResellers}
                          t={t}
                          getResellerFavicon={getResellerFaviconForTable}
                        />} 
                      />
                      {allResellers.map((reseller, index) => {
                        const isLamptwist = reseller.toLowerCase().includes('lamptwist');
                        const strokeColor = isLamptwist 
                          ? 'hsl(47 96% 53%)'
                          : getChartColor(index);
                        const isDisabled = disabledResellers.includes(reseller);
                        
                        return (
                          <Line
                            key={reseller}
                            dataKey={reseller}
                            type="monotone"
                            stroke={strokeColor}
                            strokeWidth={2.5}
                            dot={{ r: 4, fill: strokeColor, strokeWidth: 2, stroke: '#fff' }}
                            activeDot={{ r: 5, strokeWidth: 2, stroke: strokeColor, fill: strokeColor }}
                            hide={isDisabled}
                          />
                        );
                      })}
                    </LineChart>
                  </ChartContainer>
                )}
              </CardContent>
            </Card>

            {/* Summary by Market */}
            {Object.keys(summaryByMarket).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {t('summaryByMarket')} {summaryLatestDate && `(${summaryLatestDate.toLocaleDateString()})`}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {['BE', 'NL', 'FR', 'GB', 'DE', 'IT'].map((countryCode) => {
                      const retailers = summaryByMarket[countryCode];
                      if (!retailers || retailers.length === 0) return null;
                      
                      const flagCode = getCountryFlagCode(countryCode.toLowerCase());
                      
                      return (
                        <div key={countryCode} className="border rounded-xl p-4 bg-card hover:shadow-md transition-shadow">
                          <div className="flex items-center gap-2 mb-4 pb-3 border-b">
                            {flagCode ? (
                              <ReactCountryFlag
                                countryCode={flagCode}
                                svg
                                style={{
                                  width: '1.5em',
                                  height: '1.5em',
                                }}
                              />
                            ) : (
                              <Globe className="h-5 w-5 text-muted-foreground" />
                            )}
                            <h4 className="font-semibold text-sm">{countryCode}</h4>
                          </div>
                          <div className="space-y-3">
                            {retailers.map((retailer, idx) => {
                              const favicon = getResellerFaviconForTable(retailer.hostname);
                              const price = parseFloat(retailer.price_reseller);
                              const discount = parseFloat(retailer.discount);
                              const currency = countryCode === 'GB' ? '£' : '€';
                              const discountValue = parseFloat(discount);
                              
                              return (
                                <div 
                                  key={idx} 
                                  className="flex items-start justify-between gap-2 p-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                                  onClick={() => navigate(`/products?tab=priceMonitoring&reseller=${retailer.hostname}`)}
                                >
                                  <div className="flex items-center gap-2 min-w-0 flex-1">
                                    {favicon && (
                                      <img
                                        src={favicon}
                                        alt={t('resellerFavicon')}
                                        className="w-4 h-4 rounded flex-shrink-0"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                      />
                                    )}
                                    <span className="font-medium text-sm truncate">{retailer.hostname}</span>
                                  </div>
                                  <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                                    <span className="text-sm font-semibold">{currency}{price.toFixed(2)}</span>
                                    <span className={`text-xs font-medium ${
                                      discountValue > 0 ? 'text-red-600' :
                                      discountValue < 0 ? 'text-green-600' :
                                      'text-muted-foreground'
                                    }`}>
                                      {discountValue < 0 ? '+' : ''}{Math.abs(discountValue).toFixed(2)}%
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {Object.keys(summaryByMarket).filter(c => !['BE', 'NL', 'FR', 'GB', 'DE', 'IT'].includes(c)).length > 0 && (
                    <div className="mt-6 pt-6 border-t">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Object.keys(summaryByMarket)
                          .filter(countryCode => !['BE', 'NL', 'FR', 'GB', 'DE', 'IT'].includes(countryCode))
                          .map((countryCode) => {
                            const retailers = summaryByMarket[countryCode];
                            const flagCode = getCountryFlagCode(countryCode.toLowerCase());
                            
                            return (
                              <div key={countryCode} className="border rounded-xl p-4 bg-card hover:shadow-md transition-shadow">
                                <div className="flex items-center gap-2 mb-4 pb-3 border-b">
                                  {flagCode ? (
                                    <ReactCountryFlag
                                      countryCode={flagCode}
                                      svg
                                      style={{
                                        width: '1.5em',
                                        height: '1.5em',
                                      }}
                                    />
                                  ) : (
                                    <Globe className="h-5 w-5 text-muted-foreground" />
                                  )}
                                  <h4 className="font-semibold text-sm">{countryCode}</h4>
                                </div>
                                <div className="space-y-3">
                                  {retailers.map((retailer, idx) => {
                                    const favicon = getResellerFaviconForTable(retailer.hostname);
                                    const price = parseFloat(retailer.price_reseller);
                                    const discount = parseFloat(retailer.discount);
                                    const currency = countryCode === 'GB' ? '£' : '€';
                                    const discountValue = parseFloat(discount);
                                    
                                    return (
                                      <div 
                                        key={idx} 
                                        className="flex items-start justify-between gap-2 p-2 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                                        onClick={() => navigate(`/products?tab=priceMonitoring&reseller=${retailer.hostname}`)}
                                      >
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                          {favicon && (
                                            <img
                                              src={favicon}
                                              alt={t('resellerFavicon')}
                                              className="w-4 h-4 rounded flex-shrink-0"
                                              onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                          )}
                                          <span className="font-medium text-sm truncate">{retailer.hostname}</span>
                                        </div>
                                        <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                                          <span className="text-sm font-semibold">{currency}{price.toFixed(2)}</span>
                                          <span className={`text-xs font-medium ${
                                            discountValue > 0 ? 'text-red-600' :
                                            discountValue < 0 ? 'text-green-600' :
                                            'text-muted-foreground'
                                          }`}>
                                            {discountValue < 0 ? '+' : ''}{Math.abs(discountValue).toFixed(2)}%
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Average Discounts Table as DataTable (same style as dashboard) */}
            {averageDiscounts.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('averageDiscounts')} ({t(`timeRange.${timeRange}`)})</CardTitle>
                </CardHeader>
                <CardContent>
                  <AverageDiscountsTable data={averageDiscounts} t={t} getResellerFaviconForTable={getResellerFaviconForTable} />
                </CardContent>
              </Card>
            )}

            {/* Product URLs */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">{t('urls.title')}</CardTitle>
                <Button onClick={() => setShowAddUrlModal(true)} size="sm">
                  {t('urls.addUrl')}
                </Button>
              </CardHeader>
              <CardContent>
                {loadingUrls ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
                  </div>
                ) : (
                  <>
                    {/* Twistscrape URLs */}
                    {productUrls.twistscrape && productUrls.twistscrape.length > 0 && (
                      <div className="mb-6">
                        <div className="space-y-2">
                          {groupUrlsByBaseDomain(productUrls.twistscrape).map((group, groupIdx) => (
                            <div key={groupIdx} className="p-3 border rounded-lg space-y-3">
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2 min-w-0">
                                  {group.baseDomain && (
                                    <img
                                      src={getResellerFaviconForTable(group.baseDomain + (group.extensions[0] || ''))}
                                      alt={t('resellerFavicon')}
                                      className="w-4 h-4 rounded flex-shrink-0"
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                  )}
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-sm font-medium truncate">{group.baseDomain}</span>
                                    {group.extensions.length > 0 && (
                                      <span className="text-xs text-muted-foreground">
                                        {group.extensions.join(', ')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="space-y-2">
                                {group.urls.map((urlItem, urlIdx) => {
                                  const urlValue = urlItem.original_url || urlItem.l_url;
                                  if (!urlValue) return null;
                                  return (
                                    <div key={urlIdx} className="flex items-center gap-2">
                                      <a
                                        href={urlValue}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-primary hover:underline break-all"
                                        title={urlValue}
                                      >
                                        {urlValue}
                                      </a>
                                      <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Custom Brand URLs */}
                    {productUrls.brand_custom && productUrls.brand_custom.length > 0 && (
                      <div>
                        <div className="space-y-2">
                          {groupUrlsByBaseDomain(productUrls.brand_custom).map((group, groupIdx) => (
                            <div key={groupIdx} className="p-3 border rounded-lg space-y-3">
                              <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="flex items-center gap-2 min-w-0">
                                  {group.baseDomain && (
                                    <img
                                      src={getResellerFaviconForTable(group.baseDomain + (group.extensions[0] || ''))}
                                      alt={t('resellerFavicon')}
                                      className="w-4 h-4 rounded flex-shrink-0"
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                  )}
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-sm font-medium truncate">{group.baseDomain}</span>
                                    {group.extensions.length > 0 && (
                                      <span className="text-xs text-muted-foreground">
                                        {group.extensions.join(', ')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {group.urls[0]?.created_at && (
                                  <span className="text-xs text-muted-foreground">
                                    {formatDate(group.urls[0].created_at)}
                                  </span>
                                )}
                              </div>
                              <div className="space-y-2">
                                {group.urls.map((urlItem, urlIdx) => {
                                  const urlValue = urlItem.url || urlItem.original_url || urlItem.l_url;
                                  if (!urlValue) return null;
                                  return (
                                    <div key={urlItem.id || urlIdx} className="flex items-center justify-between gap-2">
                                      <div className="flex items-center gap-2 min-w-0">
                                        <a
                                          href={urlValue}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-sm text-primary hover:underline break-all"
                                          title={urlValue}
                                        >
                                          {urlValue}
                                        </a>
                                        <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                      </div>
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        <button
                                          className="p-1 hover:bg-muted rounded"
                                          title={t('urls.edit')}
                                          onClick={() => handleEditUrl(urlItem)}
                                        >
                                          <Edit className="h-4 w-4 text-muted-foreground" />
                                        </button>
                                        <button
                                          className="p-1 hover:bg-muted rounded text-red-600"
                                          title={t('urls.delete')}
                                          onClick={() => handleDeleteUrl(urlItem.id)}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* No URLs message */}
                    {(!productUrls.twistscrape || productUrls.twistscrape.length === 0) && 
                     (!productUrls.brand_custom || productUrls.brand_custom.length === 0) && (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>{t('urls.noUrls')}</p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Add URL Modal */}
            <Dialog open={showAddUrlModal} onOpenChange={setShowAddUrlModal}>
              <DialogContent className="sm:max-w-[650px]">
                <DialogHeader>
                  <DialogTitle className="text-xl">
                    {t('urls.addUrlTitle')}
                  </DialogTitle>
                  <DialogDescription>
                    {t('urls.addUrlDescription')}
                  </DialogDescription>
                </DialogHeader>
                
                {/* Product info with image */}
                {product && (
                  <div className="flex items-center gap-3 px-6 py-3 bg-muted/30 rounded-lg">
                    {product.v_image_link ? (
                      <img
                        src={product.v_image_link}
                        alt={product.p_title || t('productImage')}
                        className="w-16 h-16 object-cover rounded border"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/64';
                        }}
                      />
                    ) : (
                      <div className="w-16 h-16 bg-muted rounded border flex items-center justify-center text-xs text-muted-foreground">
                        {t('noImage')}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">
                        {product.p_title || t('productImage')}
                      </div>
                      {product.v_variant_title && (
                        <div className="text-xs text-muted-foreground truncate">
                          {product.v_variant_title}
                        </div>
                      )}
                      {product.v_vendor_code && (
                        <div className="text-xs text-muted-foreground truncate">
                          {product.v_vendor_code}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <form id="add-url-form" onSubmit={(e) => { e.preventDefault(); handleAddUrl(); }} className="space-y-4 px-6">
                  {/* Info about approved resellers */}
                  <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
                    <p className="text-blue-900 dark:text-blue-100 mb-2">
                      {t('urls.approvedResellersInfo')}
                    </p>
                    <a
                      href="/resellers?tab=supported"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                    >
                      {t('urls.viewApprovedResellers')}
                    </a>
                  </div>
                  
                  {/* Multiple URL inputs */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium block">
                      {t('urls.urls')}
                    </label>
                    {urlInputs.map((input, index) => (
                      <div key={input.id} className="flex gap-2 items-start">
                        <div className="flex-1 relative">
                          <Input
                            type="text"
                            placeholder={t('urls.urlPlaceholder')}
                            value={input.url}
                            onChange={(e) => handleUrlInputChange(input.id, e.target.value)}
                            className={`w-full pr-10 ${
                              input.status === 'valid' ? 'border-green-500' : 
                              input.status === 'invalid' ? 'border-red-500' : ''
                            }`}
                          />
                          {input.status === 'valid' && (
                            <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                          )}
                          {input.status === 'invalid' && (
                            <X className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />
                          )}
                        </div>
                        {urlInputs.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeUrlInput(input.id)}
                            className="mt-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addUrlInput}
                      className="w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {t('urls.addAnother')}
                    </Button>
                  </div>
                  
                  {urlError && (
                    <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm p-3 rounded-md">
                      {urlError}
                    </div>
                  )}
                </form>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowAddUrlModal(false);
                      setUrlInputs([{ id: 1, url: '', status: null }]);
                      setNewUrl({ url: '' });
                      setUrlError('');
                    }}
                  >
                    {t('urls.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    form="add-url-form"
                    disabled={addingUrl || !urlInputs.some(input => input.url.trim() && input.status === 'valid')}
                  >
                    {addingUrl ? t('urls.adding') : t('urls.add')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Edit URL Modal */}
            <Dialog open={showEditUrlModal} onOpenChange={setShowEditUrlModal}>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle className="text-xl">
                    {t('urls.editUrlTitle')}
                  </DialogTitle>
                  <DialogDescription>
                    {t('urls.editUrlDescription')}
                  </DialogDescription>
                </DialogHeader>
                
                {editingUrl && (
                  <form id="edit-url-form" onSubmit={(e) => { e.preventDefault(); handleUpdateUrl(); }} className="space-y-4 px-6">
                    <div>
                      <Label htmlFor="edit-url" className="text-sm font-medium mb-2 block">
                        {t('urls.url')}
                      </Label>
                      <Input
                        id="edit-url"
                        type="text"
                        placeholder={t('urls.urlPlaceholder')}
                        value={editingUrl.url}
                        onChange={(e) => {
                          setEditingUrl({ ...editingUrl, url: e.target.value });
                          setUrlError('');
                        }}
                        required
                        className="w-full"
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="edit-is-active"
                        checked={editingUrl.is_active}
                        onChange={(e) => setEditingUrl({ ...editingUrl, is_active: e.target.checked })}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <Label htmlFor="edit-is-active" className="text-sm font-medium">
                        {t('urls.active')}
                      </Label>
                    </div>
                    
                    {urlError && (
                      <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm p-3 rounded-md">
                        {urlError}
                      </div>
                    )}
                  </form>
                )}

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowEditUrlModal(false);
                      setEditingUrl(null);
                      setUrlError('');
                    }}
                  >
                    {t('urls.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    form="edit-url-form"
                    disabled={updatingUrl || !editingUrl?.url.trim()}
                  >
                    {updatingUrl ? t('urls.updating') : t('urls.update')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle className="text-xl">
                    {t('urls.confirmDelete')}
                  </DialogTitle>
                  <DialogDescription>
                    {t('urls.confirmDeleteDescription')}
                  </DialogDescription>
                </DialogHeader>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeletingUrlId(null);
                    }}
                    disabled={deletingUrl}
                  >
                    {t('urls.cancel')}
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={confirmDeleteUrl}
                    disabled={deletingUrl}
                  >
                    {deletingUrl ? t('urls.deleting') : t('urls.delete')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Report Issue Modal */}
            <Dialog open={reportModalOpen} onOpenChange={setReportModalOpen}>
              <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                  <DialogTitle className="text-xl">{t('report.title')}</DialogTitle>
                </DialogHeader>

                <div className="space-y-3 px-6 pb-3 pt-4">
                  {/* Wrong RRP */}
                  <div
                    className="flex flex-col space-y-2 border rounded-lg p-3 bg-muted/30 cursor-pointer"
                    onClick={(e) => {
                      if (e.target.tagName === 'INPUT' && e.target.type !== 'checkbox') return;
                      setReportIssues((prev) => ({ ...prev, wrong_rrp: !prev.wrong_rrp }));
                    }}
                  >
                    <label htmlFor="issue-wrong-rrp" className="flex items-center gap-2 cursor-pointer text-sm font-medium" onClick={(e) => e.preventDefault()}>
                      <input
                        type="checkbox"
                        id="issue-wrong-rrp"
                        checked={reportIssues.wrong_rrp}
                        onChange={() => {}}
                        className="pointer-events-none"
                      />
                      <span className="flex-1">
                        {(() => {
                          const currentRrp = product?.v_compareAtPrice;
                          return currentRrp ? t('report.wrongRrpCurrent', { rrp: parseFloat(currentRrp).toFixed(2) }) : t('report.wrongRrp');
                        })()}
                      </span>
                      {existingReportTypes.wrong_rrp && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteReport('wrong_rrp');
                          }}
                          className="ml-auto p-1 hover:bg-destructive/10 rounded text-destructive"
                          title={t('report.cancelReport')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </label>
                    {reportIssues.wrong_rrp && (
                      <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                        <Label className="text-sm font-medium">{t('report.newRrp')}</Label>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder={t('report.enterNewRrp')}
                          value={reportRrp}
                          onChange={(e) => setReportRrp(e.target.value)}
                        />
                      </div>
                    )}
                  </div>

                  {/* Wrong EAN */}
                  <div
                    className="flex flex-col space-y-2 border rounded-lg p-3 bg-muted/30 cursor-pointer"
                    onClick={(e) => {
                      if (e.target.tagName === 'INPUT' && e.target.type !== 'checkbox') return;
                      setReportIssues((prev) => ({ ...prev, wrong_ean: !prev.wrong_ean }));
                    }}
                  >
                    <label htmlFor="issue-wrong-ean" className="flex items-center gap-2 cursor-pointer text-sm font-medium" onClick={(e) => e.preventDefault()}>
                      <input
                        type="checkbox"
                        id="issue-wrong-ean"
                        checked={reportIssues.wrong_ean}
                        onChange={() => {}}
                        className="pointer-events-none"
                      />
                      <span className="flex-1">
                        {(() => {
                          const currentEan = product?.v_ean;
                          return currentEan ? t('report.wrongEanCurrent', { ean: currentEan }) : t('report.wrongEan');
                        })()}
                      </span>
                      {existingReportTypes.wrong_ean && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteReport('wrong_ean');
                          }}
                          className="ml-auto p-1 hover:bg-destructive/10 rounded text-destructive"
                          title={t('report.cancelReport')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </label>
                    {reportIssues.wrong_ean && (
                      <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                        <Label className="text-sm font-medium">{t('report.newEan')}</Label>
                        <Input
                          type="text"
                          placeholder={t('report.enterNewEan')}
                          value={reportEan}
                          onChange={(e) => setReportEan(e.target.value)}
                        />
                      </div>
                    )}
                  </div>

                  {/* Wrong Vendor Code */}
                  <div
                    className="flex flex-col space-y-2 border rounded-lg p-3 bg-muted/30 cursor-pointer"
                    onClick={(e) => {
                      if (e.target.tagName === 'INPUT' && e.target.type !== 'checkbox') return;
                      setReportIssues((prev) => ({ ...prev, wrong_vendor_code: !prev.wrong_vendor_code }));
                    }}
                  >
                    <label htmlFor="issue-wrong-vendor-code" className="flex items-center gap-2 cursor-pointer text-sm font-medium" onClick={(e) => e.preventDefault()}>
                      <input
                        type="checkbox"
                        id="issue-wrong-vendor-code"
                        checked={reportIssues.wrong_vendor_code}
                        onChange={() => {}}
                        className="pointer-events-none"
                      />
                      <span className="flex-1">
                        {(() => {
                          const currentVendorCode = product?.v_vendor_code;
                          return currentVendorCode ? t('report.wrongVendorCodeCurrent', { code: currentVendorCode }) : t('report.wrongVendorCode');
                        })()}
                      </span>
                      {existingReportTypes.wrong_vendor_code && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteReport('wrong_vendor_code');
                          }}
                          className="ml-auto p-1 hover:bg-destructive/10 rounded text-destructive"
                          title={t('report.cancelReport')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </label>
                    {reportIssues.wrong_vendor_code && (
                      <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                        <Label className="text-sm font-medium">{t('report.newVendorCode')}</Label>
                        <Input
                          type="text"
                          placeholder={t('report.enterNewVendorCode')}
                          value={reportVendorCode}
                          onChange={(e) => setReportVendorCode(e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <DialogFooter className="gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeReportModal}
                    disabled={submittingReport}
                  >
                    {t('report.cancel')}
                  </Button>
                  <Button
                    type="button"
                    onClick={submitReport}
                    disabled={
                      submittingReport ||
                      (!reportIssues.wrong_rrp && !reportIssues.wrong_ean && !reportIssues.wrong_vendor_code) ||
                      (reportIssues.wrong_rrp && !reportRrp) ||
                      (reportIssues.wrong_ean && !reportEan.trim()) ||
                      (reportIssues.wrong_vendor_code && !reportVendorCode.trim())
                    }
                  >
                    {submittingReport ? t('report.submitting') : t('report.submit')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

          </>
        )}
      </div>
    </Layout>
  );
};

export default ProductDetail;

